(() => {
  /*
    Navigation helpers:
    - Still full-page navigations
    - Quietly warm next HTML so the next shell can paint sooner
    - Leave bar / veil live in tt-product-page-loader.js
  */
  if (!document.body || document.documentElement.classList.contains('shopify-design-mode')) return;

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const saveData = Boolean(connection && connection.saveData);
  const slowNet =
    connection &&
    (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g');
  const isMobile = window.matchMedia('(max-width: 749px)').matches;
  const isHome = document.body.classList.contains('template-index');
  const isProduct = document.body.classList.contains('template-product');
  const skipMobileAutoWarm = isMobile && (isHome || isProduct);

  if (saveData || slowNet) return;

  const warmed = new Set();
  const MAX_WARM = isMobile ? 8 : 20;
  let prerenderCount = 0;
  const MAX_PRERENDER = 2;

  const isSkippablePath = (pathname) =>
    /^\/(cart|checkout|account|orders|apps|admin|tools|password)(\/|$)/i.test(pathname);

  const toSameOriginUrl = (href) => {
    if (!href || href.charAt(0) === '#') return null;
    let url;
    try {
      url = new URL(href, window.location.origin);
    } catch (error) {
      return null;
    }
    if (url.origin !== window.location.origin) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (isSkippablePath(url.pathname)) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return null;
    return url;
  };

  const supportsSpeculation = () => {
    try {
      return Boolean(HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules'));
    } catch (error) {
      return false;
    }
  };

  const warmDocument = (href, { high = false } = {}) => {
    const url = toSameOriginUrl(href);
    if (!url || warmed.size >= MAX_WARM || warmed.has(url.href)) return false;

    warmed.add(url.href);
    const path = url.pathname + url.search;

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = path;
    document.head.appendChild(link);

    fetch(path, {
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { Accept: 'text/html', Purpose: 'prefetch', 'Sec-Purpose': 'prefetch' },
      priority: high ? 'high' : 'low',
    }).catch(() => {});

    return true;
  };

  const prerenderDocument = (href) => {
    const url = toSameOriginUrl(href);
    if (!url || !supportsSpeculation() || prerenderCount >= MAX_PRERENDER) return false;
    prerenderCount += 1;
    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify({
      prerender: [
        {
          source: 'list',
          urls: [url.pathname + url.search],
          eagerness: 'immediate',
        },
      ],
    });
    document.head.appendChild(script);
    return true;
  };

  // Finger down → warm next document so shell HTML is often already cached.
  document.addEventListener(
    'pointerdown',
    (event) => {
      const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      const href = anchor.getAttribute('href');
      warmDocument(href, { high: true });
      if (!isMobile) prerenderDocument(href);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'touchstart',
    (event) => {
      const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
      if (!anchor || anchor.target === '_blank') return;
      warmDocument(anchor.getAttribute('href'), { high: true });
    },
    { capture: true, passive: true }
  );

  const warmSelectorList = (selectors, limit) => {
    let count = 0;
    document.querySelectorAll(selectors.join(',')).forEach((anchor) => {
      if (count >= limit) return;
      if (warmDocument(anchor.getAttribute('href'))) count += 1;
    });
  };

  const warmEarly = () => {
    warmSelectorList(
      [
        '#menu-drawer a[href]',
        '.menu-drawer a[href]',
        'header a[href*="/collections/"]',
        'header a[href*="/products/"]',
        'header a[href*="/pages/"]',
        '#MainContent a[href*="/products/"]',
        '#MainContent a[href*="/collections/"]',
      ],
      isMobile ? 6 : 14
    );
  };

  const runWhenIdle = (fn, timeout) => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout });
    else window.setTimeout(fn, Math.min(timeout, 800));
  };

  /* Mobile homepage: do not prefetch other documents during first load.
     Pointer/touch still warms the link the shopper actually taps. */
  if (skipMobileAutoWarm) {
    /* skip automatic warmEarly on mobile home and product pages */
  } else if (isMobile) {
    const startMobileWarm = () => runWhenIdle(warmEarly, 1800);
    if (document.readyState === 'complete') startMobileWarm();
    else window.addEventListener('load', startMobileWarm, { once: true });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', warmEarly, { once: true });
  } else {
    warmEarly();
  }

  const warmLater = () => {
    warmSelectorList(
      ['#MainContent .card a[href]', '#MainContent a[href*="/products/"]', 'footer a[href*="/collections/"]'],
      isMobile ? 4 : 10
    );
  };
  if (skipMobileAutoWarm) {
    /* skip automatic warmLater on mobile home and product pages */
  } else if (document.readyState === 'complete') {
    runWhenIdle(warmLater, isMobile ? 2200 : 200);
  } else {
    window.addEventListener(
      'load',
      () => runWhenIdle(warmLater, isMobile ? 2200 : 200),
      { once: true }
    );
  }

  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const anchor =
            entry.target.tagName === 'A'
              ? entry.target
              : entry.target.querySelector('a[href*="/products/"], a[href*="/collections/"]');
          if (anchor) warmDocument(anchor.getAttribute('href'));
          io.unobserve(entry.target);
        });
      },
      { rootMargin: isMobile ? '240px 0px' : '320px 0px' }
    );

    const observe = () => {
      document
        .querySelectorAll(
          '#MainContent .card, #MainContent .card-wrapper, #MainContent li.grid__item, #MainContent a[href*="/products/"]'
        )
        .forEach((node) => io.observe(node));
    };
    if (skipMobileAutoWarm) {
      /* skip product-link observers on mobile home and product pages */
    } else if (isMobile) {
      const startObserve = () => runWhenIdle(observe, 2200);
      if (document.readyState === 'complete') startObserve();
      else window.addEventListener('load', startObserve, { once: true });
    } else if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observe, { once: true });
    } else {
      observe();
    }
  }

  if (supportsSpeculation() && !isMobile) {
    const rules = document.createElement('script');
    rules.type = 'speculationrules';
    rules.textContent = JSON.stringify({
      prefetch: [
        {
          where: {
            and: [
              { href_matches: '/*' },
              {
                not: {
                  href_matches: ['/cart*', '/checkout*', '/account*', '/orders*', '/password*', '/admin*'],
                },
              },
            ],
          },
          eagerness: 'moderate',
        },
      ],
      prerender: [
        {
          where: {
            and: [
              { href_matches: '/*' },
              {
                not: {
                  href_matches: ['/cart*', '/checkout*', '/account*', '/orders*', '/password*', '/admin*'],
                },
              },
            ],
          },
          eagerness: 'conservative',
        },
      ],
    });
    document.head.appendChild(rules);
  }
})();
